import {
  isAssessmentAddQuestionsPath,
  normalizeFocusWorkflowPath,
} from "@/lib/focus-workflow"

export const IMPORT_REVIEW_SECONDARY_PANEL_ID = "import-review"

/** Same-origin return path only. Rejects protocol-relative `//…`. */
export function safeReturnPath(value: string | null | undefined): string | null {
  if (!value || !value.startsWith("/") || value.startsWith("//")) return null
  return value
}

/** Keep the page the author left when opening import review. */
export function withReturnFrom(href: string, from?: string | null): string {
  const safe = safeReturnPath(from)
  if (!safe) return href
  const [path, query = ""] = href.split("?")
  const params = new URLSearchParams(query)
  params.set("from", safe)
  return `${path}?${params.toString()}`
}

const QUESTION_IMPORT_REVIEW_PATH = /^\/question-bank\/import(\/[^/]+)?$/

function searchParams(search: string): URLSearchParams {
  return new URLSearchParams(search.startsWith("?") ? search.slice(1) : search)
}

/** Add Questions `?review=` or the bank import review route. */
export function isImportReviewSecondaryPanelRoute(
  pathname: string,
  search: string
): boolean {
  if (isAssessmentAddQuestionsPath(pathname)) {
    return searchParams(search).has("review")
  }
  return QUESTION_IMPORT_REVIEW_PATH.test(normalizeFocusWorkflowPath(pathname))
}

export function shouldImportReviewSecondaryPanelBeOpen(
  pathname: string,
  search: string
): boolean {
  return isImportReviewSecondaryPanelRoute(pathname, search)
}

/**
 * Review is a URL-addressed screen, so the rail cannot outlive the URL.
 *
 * Left open, the route's own panel rule and this one disagree about who owns
 * the shell rail, and the panel mounts and unmounts on every render.
 */
export function shouldCloseStaleImportReviewPanel(input: {
  pathname: string
  search: string
  activePanel: string | null
}): boolean {
  return (
    input.activePanel === IMPORT_REVIEW_SECONDARY_PANEL_ID &&
    !shouldImportReviewSecondaryPanelBeOpen(input.pathname, input.search)
  )
}

export function isImportReviewSecondaryPanelVisible(
  pathname: string,
  search: string,
  activePanel: string | null
): boolean {
  return (
    shouldImportReviewSecondaryPanelBeOpen(pathname, search) ||
    activePanel === IMPORT_REVIEW_SECONDARY_PANEL_ID
  )
}
